import { useState } from 'react';
import { AIRPORTS, MAX_PLACES, PAGE_PLACE_KINDS, PLACE_SLOTS, type PlaceInput, type PlaceKind, type Point, type SavedPlace } from '@rydres/shared';
import { AddressInput, type GeoApi } from './geo.js';
import { Badge, Button, Field, Input, Notice } from './primitives.js';

export type AddressField = 'pickup' | 'dropoff';

/** Remembers which address field had focus last, so place buttons fill that one (pickup at first). */
export function useAddressTarget(initial: AddressField = 'pickup') {
  const [target, setTarget] = useState<AddressField>(initial);
  return { target, setTarget, focus: (field: AddressField) => ({ onFocus: () => setTarget(field) }) };
}

/** Quick-fill buttons under pickup and drop-off fields. */
export function PlaceButtons({
  places,
  target,
  onPick,
}: {
  places: ReadonlyArray<{ label: string; address: string; point?: Point | null }>;
  target: AddressField;
  onPick: (address: string, point: Point | null) => void;
}) {
  if (!places.length) return null;
  const field = target === 'pickup' ? 'pickup' : 'drop-off';
  return (
    <div>
      <div className="mb-1.5 text-xs text-muted">Tap a place to fill the {field}</div>
      <div className="flex flex-wrap gap-2">
        {places.map((p) => (
          <button
            key={`${p.label}|${p.address}`}
            type="button"
            aria-label={`Fill ${field} with ${p.label}`}
            title={p.address}
            // Keeps the address field focused, so the phone keyboard stays where it was.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(p.address, p.point ?? null)}
            className="min-h-11 rounded-full border border-line bg-card px-3.5 text-sm font-bold text-ink hover:bg-card2"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type Draft = PlaceInput & { index: number | null };

/**
 * The person's saved places: one-tap airports, the Home/Work/Friend slots and any other place.
 * Every change saves the whole list at once. Mount it once the places have loaded.
 */
export function PlacesEditor({
  places,
  onSave,
  pageToggle,
  homeNote,
  geo,
}: {
  places: SavedPlace[];
  onSave: (places: PlaceInput[]) => Promise<SavedPlace[]>;
  /** Address suggestions; without them the address is typed. */
  geo?: GeoApi | null;
  /** Drivers may show airports and other places as buttons on their booking page. */
  pageToggle?: boolean;
  /** Shown under the first home place. */
  homeNote?: string;
}) {
  const [list, setList] = useState(places);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (next: PlaceInput[]) => {
    setBusy(true);
    setError(null);
    try {
      setList(await onSave(next));
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not save your places.');
    } finally {
      setBusy(false);
    }
  };
  const taken = new Set(list.map((p) => p.label.toLowerCase()));
  const slots = PLACE_SLOTS.filter((s) => !taken.has(s.label.toLowerCase()));
  const airports = AIRPORTS.filter((a) => !taken.has(a.label.toLowerCase()) && !list.some((p) => p.address === a.address));
  const firstHome = list.findIndex((p) => p.kind === 'home');
  const submit = () => {
    if (!draft) return;
    const { index, ...place } = draft;
    const item = { ...place, label: place.label.trim(), address: place.address.trim() };
    if (list.some((p, i) => i !== index && p.label.toLowerCase() === item.label.toLowerCase()))
      return setError(`You already have a place called "${item.label}".`);
    void save(index === null ? [...list, item] : list.map((p, i) => (i === index ? item : p)));
  };

  return (
    <div className="space-y-3">
      {list.length === 0 && !draft && <p className="text-sm text-muted">No places yet. Add the ones you use most; they show as buttons under pickup and drop-off.</p>}
      {list.length > 0 && (
        <ul className="space-y-2">
          {list.map((p, i) =>
            draft?.index === i ? null : (
              <li key={p.label} className="flex items-start gap-3 rounded-2xl border border-line bg-card p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 font-bold">
                    {p.label}
                    {pageToggle && p.showOnPage && <Badge value="on your page" tone="info" />}
                  </div>
                  <div className="break-words text-sm text-ink2">{p.address}</div>
                  {homeNote && i === firstHome && <div className="mt-1 text-xs text-muted">{homeNote}</div>}
                </div>
                <button type="button" disabled={busy || draft !== null} onClick={() => setDraft({ ...p, index: i })} className="shrink-0 py-1 text-sm font-bold text-accent-text disabled:opacity-40">
                  Edit
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      {draft && (
        <div className="space-y-3 rounded-2xl border border-line bg-card p-3">
          <Field label="Name">
            <Input value={draft.label} maxLength={40} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Mom's house" />
          </Field>
          <Field label="Address">
            <AddressInput autoFocus geo={geo} value={draft.address} onChange={({ address, point }) => setDraft({ ...draft, address, point })} placeholder="Street, city" />
          </Field>
          {pageToggle && PAGE_PLACE_KINDS.includes(draft.kind) && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.showOnPage ?? false} onChange={(e) => setDraft({ ...draft, showOnPage: e.target.checked })} />
              Show as a button on my booking page
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <Button small disabled={busy || !draft.label.trim() || draft.address.trim().length < 3} onClick={submit}>
              {busy ? 'Saving…' : 'Save place'}
            </Button>
            <Button small variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
              Cancel
            </Button>
            {draft.index !== null && (
              <Button small variant="danger" disabled={busy} onClick={() => void save(list.filter((_, i) => i !== draft.index))}>
                Remove
              </Button>
            )}
          </div>
        </div>
      )}

      {!draft && list.length < MAX_PLACES && (
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Add a place</div>
          <div className="flex flex-wrap gap-2">
            {airports.map((a) => (
              <Chip
                key={a.code}
                disabled={busy}
                label={a.label}
                onClick={() => void save([...list, { kind: 'airport', label: a.label, address: a.address, point: a.point, showOnPage: !!pageToggle }])}
              />
            ))}
            {slots.map((s) => (
              <Chip key={s.label} disabled={busy} label={s.label} onClick={() => setDraft({ kind: s.kind, label: s.label, address: '', showOnPage: false, index: null })} />
            ))}
            <Chip disabled={busy} label="Other place" onClick={() => setDraft({ kind: 'other' as PlaceKind, label: '', address: '', showOnPage: false, index: null })} />
          </div>
        </div>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
    </div>
  );
}

function Chip({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className="min-h-11 rounded-full border border-dashed border-line px-3.5 text-sm font-bold text-ink2 hover:bg-card2 disabled:opacity-40">
      + {label}
    </button>
  );
}
